import React from "react";
import { ShortcutId } from "../game/shortcuts";
import { classNames } from "../utils/classNames";
import { Tooltip } from "./Tooltip";

/**
 * The ✕ that puts a paper object away — the manual, the journal, a
 * station sheet, the plan folder. Every panel in the shell is a physical
 * thing, and nothing printed on a physical thing closes it; the ✕ is the
 * computer's, so it sits *off* the object, a round chip floating up and
 * to the right of its top-right corner in the same dark chrome as the
 * HUD's chips, and never on the paper. The frame that positions the
 * object (Modal, SheetShell,
 * or the world-pinned card itself) renders it; the object doesn't know
 * it's there.
 *
 * The parent must be `relative`: the button anchors to the parent's
 * corner. Its accessible name is the verb the tooltip shows, in the
 * fiction's words ("Put the sheet away", "Stay in the shop"), with the
 * key that does the same beside it.
 *
 * Only surfaces whose one way out is to stop looking at them get a ✕.
 * A receipt's "Keep Shopping", a trip's "Head Home", the coach card's
 * "Skip" are the exit in the fiction, and stay the only one.
 */
export const CloseButton: React.FC<{
  /** What closing does, in the fiction's words. The accessible name too. */
  label: string;
  /** The key that closes the same thing, for the tooltip's cap. */
  shortcut?: ShortcutId;
  onClose: () => void;
  className?: string;
}> = ({ label, shortcut = "close-modal", onClose, className }) => (
  <Tooltip content={label} shortcut={shortcut} placement="left">
    <button
      type="button"
      onClick={onClose}
      aria-label={label}
      data-sfx="ui-back"
      data-testid="close-button"
      className={classNames(
        // A round chip floating a hair up and to the right of the corner,
        // clear of the object on both sides
        "hud-chip absolute bottom-full left-full z-30 mb-1 ml-1 flex size-7 items-center justify-center rounded-full font-condensed text-base leading-none text-paper-manila/80",
        "hover:border-paper-manila/60 hover:text-paper-manila active:bg-paper-manila/10",
        className,
      )}
    >
      ✕
    </button>
  </Tooltip>
);
